import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  Button,
  DataTable,
  Field,
  Input,
  Modal,
  StateBlock,
  Textarea,
  Toggle,
  useToast,
  type Column,
} from '@w7/ui'
import { useMemo, useState, type FormEvent } from 'react'
import { cmApi, type SecurityManualGroupRow } from '@/api/cmApi'
import { authHeaders } from '@/auth/session'
import { CmPanel } from '@/features/catalog/CmPanel'
import { formatGroupCreatedAt } from './formatGroupCreatedAt'
import { GroupMemberPicker } from './GroupMemberPicker'

export { formatGroupCreatedAt }

function toDateInputValue(d: Date): string {
  const y = d.getFullYear()
  const m = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  return `${y}-${m}-${day}`
}

function dayStartIso(ymd: string): string {
  const d = new Date(`${ymd}T00:00:00`)
  return d.toISOString()
}

function dayEndIso(ymd: string): string {
  const d = new Date(`${ymd}T23:59:59.999`)
  return d.toISOString()
}

type StatusFilter = 'all' | 'active' | 'inactive'

/**
 * Lab `/manualGroupBetting` — Strix Security chrome (same pattern as All cheaters).
 */
export function ManualGroupBettingPage() {
  const toast = useToast()
  const qc = useQueryClient()
  const today = toDateInputValue(new Date())
  const [page, setPage] = useState(1)
  const [fromDraft, setFromDraft] = useState(today)
  const [toDraft, setToDraft] = useState(today)
  const [groupNameDraft, setGroupNameDraft] = useState('')
  const [statusDraft, setStatusDraft] = useState<StatusFilter>('all')
  const [applied, setApplied] = useState<{
    fromDate?: string
    toDate?: string
    groupName?: string
    status?: boolean
  }>({})
  const [modalOpen, setModalOpen] = useState(false)
  const [editing, setEditing] = useState<SecurityManualGroupRow | null>(null)
  const [groupName, setGroupName] = useState('')
  const [members, setMembers] = useState<string[]>([])
  const [comment, setComment] = useState('')
  const [pendingDelete, setPendingDelete] = useState<SecurityManualGroupRow | null>(null)
  const pageSize = 50

  const invalidate = () =>
    qc.invalidateQueries({ queryKey: ['cm', 'security', 'manual-groups'] })

  const listQ = useQuery({
    queryKey: ['cm', 'security', 'manual-groups', page, pageSize, applied],
    queryFn: () =>
      cmApi.securityManualGroupsList(authHeaders(), {
        page,
        pageSize,
        ...applied,
      }),
  })

  const resetForm = () => {
    setEditing(null)
    setGroupName('')
    setMembers([])
    setComment('')
  }

  const openCreate = () => {
    resetForm()
    setModalOpen(true)
  }

  const openEdit = (row: SecurityManualGroupRow) => {
    setEditing(row)
    setGroupName(row.groupName)
    setMembers(row.members.map((m) => m.username))
    setComment(row.comment ?? '')
    setModalOpen(true)
  }

  const saveM = useMutation({
    mutationFn: () => {
      if (!members.length || !comment.trim()) {
        throw new Error('Please select users and enter a comment.')
      }
      if (editing) {
        return cmApi.securityManualGroupsUpdate(authHeaders(), {
          id: editing.id,
          members: members.map((username) => ({ username })),
          comment: comment.trim(),
          status: editing.status,
        })
      }
      if (!groupName.trim()) {
        throw new Error('Group name is required.')
      }
      return cmApi.securityManualGroupsCreate(authHeaders(), {
        groupName: groupName.trim(),
        members: members.map((username) => ({ username })),
        comment: comment.trim(),
        status: true,
      })
    },
    onSuccess: async () => {
      toast.success(editing ? 'Group updated successfully' : 'Group created successfully')
      setModalOpen(false)
      resetForm()
      await invalidate()
    },
    onError: (err) => toast.error(err instanceof Error ? err.message : String(err)),
  })

  const toggleM = useMutation({
    mutationFn: (row: SecurityManualGroupRow) =>
      cmApi.securityManualGroupsUpdate(authHeaders(), {
        id: row.id,
        members: row.members.map((m) => ({
          username: m.username,
          memberId: m.memberId || undefined,
        })),
        comment: row.comment,
        status: !row.status,
      }),
    onSuccess: async () => {
      toast.success('Status updated successfully')
      await invalidate()
    },
    onError: (err) => toast.error(err instanceof Error ? err.message : String(err)),
  })

  const deleteM = useMutation({
    mutationFn: (row: SecurityManualGroupRow) =>
      cmApi.securityManualGroupsUpdate(authHeaders(), {
        id: row.id,
        isDeleted: true,
      }),
    onSuccess: async () => {
      toast.success('Group deleted successfully')
      setPendingDelete(null)
      await invalidate()
    },
    onError: (err) => toast.error(err instanceof Error ? err.message : String(err)),
  })

  const rows = listQ.data?.rows ?? []
  const total = listQ.data?.total ?? 0
  const totalPages = Math.max(1, Math.ceil(total / pageSize))

  const columns = useMemo((): Column<SecurityManualGroupRow>[] => {
    return [
      {
        id: 'n',
        header: 'SL No',
        width: 56,
        numeric: true,
        cell: (_row, i) => (page - 1) * pageSize + i + 1,
      },
      {
        id: 'created',
        header: 'Created Date',
        cell: (r) => formatGroupCreatedAt(r.createdAt),
      },
      { id: 'name', header: 'Group Name', cell: (r) => r.groupName || '—' },
      {
        id: 'users',
        header: 'User Name',
        cell: (r) =>
          r.members.length ? r.members.map((m) => m.username).join(', ') : '—',
      },
      { id: 'comment', header: 'Comment', cell: (r) => r.comment || '—' },
      {
        id: 'status',
        header: 'Status',
        cell: (r) => (
          <Toggle
            checked={r.status}
            label={r.status ? `Active ${r.groupName}` : `Inactive ${r.groupName}`}
            disabled={toggleM.isPending}
            onChange={() => toggleM.mutate(r)}
          />
        ),
      },
      {
        id: 'actions',
        header: 'Actions',
        cell: (r) => (
          <div className="cm-group-actions">
            <Button
              type="button"
              size="sm"
              variant="secondary"
              aria-label={`Edit group ${r.groupName}`}
              onClick={() => openEdit(r)}
            >
              Edit
            </Button>
            <Button
              type="button"
              size="sm"
              variant="danger"
              aria-label={`Delete group ${r.groupName}`}
              onClick={() => setPendingDelete(r)}
            >
              Delete
            </Button>
          </div>
        ),
      },
    ]
  }, [page, pageSize, toggleM.isPending])

  if (listQ.isLoading && !rows.length) {
    return (
      <StateBlock icon="settings" title="Loading Manual Group Betting…">
        Reading CM-API security lists.
      </StateBlock>
    )
  }

  return (
    <CmPanel
      title="Manual Group Betting"
      subtitle="Track suspected syndicate / multi-account betting groups"
      actions={
        <div className="cm-form-actions">
          <Button
            type="button"
            variant="secondary"
            busy={listQ.isFetching}
            onClick={() => void listQ.refetch()}
          >
            Refresh
          </Button>
          <Button type="button" onClick={openCreate}>
            Create Group
          </Button>
        </div>
      }
    >
      <div className="cm-security">
        <form
          className="cm-toolbar cm-group-toolbar"
          onSubmit={(e: FormEvent) => {
            e.preventDefault()
            const next: typeof applied = {}
            if (fromDraft) next.fromDate = dayStartIso(fromDraft)
            if (toDraft) next.toDate = dayEndIso(toDraft)
            if (groupNameDraft.trim()) next.groupName = groupNameDraft.trim()
            if (statusDraft === 'active') next.status = true
            if (statusDraft === 'inactive') next.status = false
            setPage(1)
            setApplied(next)
          }}
        >
          <Field label="From Date">
            {({ id, describedBy }) => (
              <Input
                id={id}
                aria-describedby={describedBy}
                type="date"
                value={fromDraft}
                onChange={(e) => setFromDraft(e.target.value)}
              />
            )}
          </Field>
          <Field label="To Date">
            {({ id, describedBy }) => (
              <Input
                id={id}
                aria-describedby={describedBy}
                type="date"
                value={toDraft}
                onChange={(e) => setToDraft(e.target.value)}
              />
            )}
          </Field>
          <div className="cm-toolbar-search">
            <Field label="Group Name">
              {({ id, describedBy }) => (
                <Input
                  id={id}
                  aria-describedby={describedBy}
                  value={groupNameDraft}
                  onChange={(e) => setGroupNameDraft(e.target.value)}
                  placeholder="Group Name"
                />
              )}
            </Field>
          </div>
          <Field label="Status">
            {({ id, describedBy }) => (
              <select
                id={id}
                aria-describedby={describedBy}
                className="w7-input"
                value={statusDraft}
                onChange={(e) => setStatusDraft(e.target.value as StatusFilter)}
              >
                <option value="all">All</option>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
            )}
          </Field>
          <Button type="submit" variant="secondary">
            Search
          </Button>
        </form>

        {listQ.isError ? (
          <div className="cm-hint" role="alert">
            <p>{(listQ.error as Error).message}</p>
            <Button type="button" variant="secondary" onClick={() => void listQ.refetch()}>
              Retry
            </Button>
          </div>
        ) : null}

        <DataTable
          label="Manual Group Betting list"
          density="compact"
          rows={rows}
          columns={columns}
          rowKey={(row) => row.id}
          loading={listQ.isFetching && !rows.length}
          empty={
            !listQ.isError ? (
              <div className="cm-security-empty">
                <StateBlock icon="search" title="No Data Found">
                  No groups match these filters.
                </StateBlock>
              </div>
            ) : null
          }
        />

        {total > pageSize ? (
          <div className="cm-security-pager">
            <Button
              type="button"
              variant="secondary"
              disabled={page <= 1}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
            >
              Previous
            </Button>
            <span className="cm-hint">
              Page {page} / {totalPages} · {total} row(s)
            </span>
            <Button
              type="button"
              variant="secondary"
              disabled={page >= totalPages}
              onClick={() => setPage((p) => p + 1)}
            >
              Next
            </Button>
          </div>
        ) : null}
      </div>

      <Modal
        open={modalOpen}
        onClose={() => {
          if (saveM.isPending) return
          setModalOpen(false)
          resetForm()
        }}
        title={editing ? 'Edit Group' : 'Add Group'}
        closeDisabled={saveM.isPending}
        footer={
          <div className="cm-form-actions">
            <Button
              type="button"
              variant="secondary"
              disabled={saveM.isPending}
              onClick={() => {
                setModalOpen(false)
                resetForm()
              }}
            >
              Close
            </Button>
            <Button
              type="button"
              busy={saveM.isPending}
              disabled={!members.length || !comment.trim() || (!editing && !groupName.trim())}
              onClick={() => saveM.mutate()}
            >
              Save
            </Button>
          </div>
        }
      >
        <div className="cm-form">
          <Field label="Group Name" required>
            {({ id, describedBy }) => (
              <Input
                id={id}
                aria-describedby={describedBy}
                required
                disabled={Boolean(editing)}
                maxLength={50}
                value={groupName}
                onChange={(e) => setGroupName(e.target.value)}
                placeholder="Enter Group Name"
              />
            )}
          </Field>
          <GroupMemberPicker value={members} onChange={setMembers} />
          <Field label="Comment" required>
            {({ id, describedBy }) => (
              <Textarea
                id={id}
                aria-describedby={describedBy}
                required
                rows={3}
                maxLength={200}
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                placeholder="Enter comment"
              />
            )}
          </Field>
          <p className="cm-hint">{comment.length}/200</p>
        </div>
      </Modal>

      <Modal
        open={pendingDelete != null}
        onClose={() => {
          if (deleteM.isPending) return
          setPendingDelete(null)
        }}
        title="Are you sure you want to delete the group?"
        subtitle={pendingDelete ? `Group: ${pendingDelete.groupName}` : undefined}
        tone="danger"
        closeDisabled={deleteM.isPending}
        footer={
          <div className="cm-form-actions">
            <Button
              type="button"
              variant="secondary"
              disabled={deleteM.isPending}
              onClick={() => setPendingDelete(null)}
            >
              No
            </Button>
            <Button
              type="button"
              variant="danger"
              busy={deleteM.isPending}
              onClick={() => {
                if (pendingDelete) deleteM.mutate(pendingDelete)
              }}
            >
              Yes
            </Button>
          </div>
        }
      >
        <p className="cm-hint">This soft-deletes the group. It will no longer appear in the list.</p>
      </Modal>
    </CmPanel>
  )
}
